/**
 * Demande — ou corrige — le nom de l'utilisateur.
 *
 * **Pourquoi elle existe.** Un accès client ouvert par lien d'invitation arrive avec une
 * adresse et rien d'autre : il n'est jamais passé par Google, qui fournit d'ordinaire le nom
 * et la photo. L'écran l'accueillait donc par un « Bonjour » sans suite, et son nom manquait
 * partout où l'agence le cherchait.
 *
 * Deux usages, un seul composant :
 *
 * - à la première connexion, quand le nom manque — la modale s'ouvre d'elle-même ;
 * - plus tard, depuis le menu du profil, pour le corriger.
 *
 * Le second n'est pas un supplément : une question posée une fois, à un moment où l'on veut
 * surtout entrer, se répond souvent de travers ou pas du tout.
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { User, X } from 'lucide-react';
import config from '../config';

const API_URL = config.API_URL;

const NomProfilModal = ({ token, user, ouvert, onClose, onEnregistre, premiereFois = false }) => {
  const [nom, setNom] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState('');
  const champ = useRef(null);

  // Repartir du nom retenu à chaque ouverture, jamais d'une saisie abandonnée.
  useEffect(() => {
    if (!ouvert) return;
    setNom(user?.name || '');
    setErreur('');
    // Le champ est le seul élément actionnable : y placer le curseur évite un clic de plus.
    const t = setTimeout(() => champ.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [ouvert, user?.name]);

  if (!ouvert) return null;

  const enregistrer = async () => {
    setEnregistrement(true);
    setErreur('');

    try {
      const { data } = await axios.put(
        `${API_URL}/api/profil/nom`,
        { nom },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        onEnregistre(data.user);
        onClose();
      } else {
        setErreur(data.error || 'Impossible d\'enregistrer votre nom');
      }
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible d\'enregistrer votre nom');
    } finally {
      setEnregistrement(false);
    }
  };

  const auClavier = (e) => {
    if (e.key === 'Enter' && nom.trim()) enregistrer();
    if (e.key === 'Escape' && !premiereFois) onClose();
  };

  return ReactDOM.createPortal(
    <div
      // À la première connexion, le clic extérieur ne ferme pas : la question doit se voir,
      // pas se congédier d'un clic distrait. Partout ailleurs, elle se ferme comme le reste.
      onClick={premiereFois ? undefined : onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '14px', padding: '28px',
          width: '100%', maxWidth: '420px', boxShadow: '0 20px 40px rgba(0,0,0,0.18)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <User size={18} color="white" />
            </div>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1f2937' }}>
              {premiereFois ? 'Comment vous appelez-vous ?' : 'Modifier mon nom'}
            </h2>
          </div>

          {/* Pas de croix à la première connexion : la seule sortie est « Plus tard », qui le
              dit en toutes lettres. */}
          {!premiereFois && (
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
              title="Fermer"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <p style={{ margin: '14px 0 18px', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
          {premiereFois
            ? 'Votre nom apparaîtra sur vos tableaux de bord et permettra à votre agence de vous reconnaître.'
            : 'Ce nom apparaît sur vos tableaux de bord et dans la liste des accès de votre agence.'}
        </p>

        <input
          ref={champ}
          type="text"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          onKeyDown={auClavier}
          placeholder="Prénom et nom"
          maxLength={60}
          style={{
            width: '100%', padding: '11px 13px', boxSizing: 'border-box',
            border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9375rem'
          }}
        />

        <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
          Connecté avec {user?.email}
        </p>

        {erreur && (
          <p style={{ margin: '12px 0 0', fontSize: '0.8125rem', color: '#dc2626' }}>{erreur}</p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
          <button
            onClick={onClose}
            disabled={enregistrement}
            style={{
              padding: '9px 16px', borderRadius: '8px', cursor: 'pointer',
              border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#374151',
              fontSize: '0.875rem'
            }}
          >
            {premiereFois ? 'Plus tard' : 'Annuler'}
          </button>
          <button
            onClick={enregistrer}
            // Un nom vide n'est pas un nom : le bouton reste inerte plutôt que d'enregistrer
            // un blanc qui ramènerait au point de départ.
            disabled={enregistrement || !nom.trim()}
            style={{
              padding: '9px 18px', borderRadius: '8px',
              cursor: enregistrement || !nom.trim() ? 'default' : 'pointer',
              border: 'none', backgroundColor: nom.trim() ? '#1f2937' : '#d1d5db',
              color: 'white', fontSize: '0.875rem', fontWeight: 500
            }}
          >
            {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NomProfilModal;
